import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, GraduationCap, Info, RotateCw } from 'lucide-react';
import type { LevelFeatures, StudyPathStepView } from '@en3/shared';
import { queryKeys } from '@/api/client';
import { toDisplayMessage } from '@/api/errors';
import { useAuthStatus } from '@/api/hooks/useAuth';
import { levelApi } from '@/api/level.api';
import { ROUTES } from '@/app/routes';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '@/components/common/States';
import { LevelGauge } from '@/components/level/LevelGauge';
import { TrendChart } from '@/components/level/TrendChart';
import { WeaknessList } from '@/components/level/WeaknessList';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { AuthRequiredNotice } from '@/pages/WordbookPage';

/**
 * 水平报告。
 *
 * **进页面只调 report，绝不自动 assess**。这是本页最容易做错、也最难被发现的一点：
 * `assess` 是写操作（会往 `level_assessments` 插一条），自动调用意味着
 * "每刷新一次页面就多一条评估记录"，趋势图会变成噪声，用户自己的历史也被污染。
 * 因此：
 * - 进页面：`GET /api/level/report`（纯读）；
 * - 用户主动点「重新评估」：`POST /api/level/assess`，并明确告知发生了什么。
 *
 * 未登录时的引导保留 `<h1>水平报告</h1>`（复用生词本页的 `AuthRequiredNotice`，
 * 那段文案与入口在两个页面里是同一份，避免各写一遍后慢慢漂移）。
 *
 * 另一个诚实性决定：**特征值展示的是实时值，而结论来自评估时的快照**。
 * `report.features` 是"现在"的统计（可能刚读完一篇），`latest.features` 是生成结论时的
 * 快照。两者混在一起展示会让用户觉得"数字对不上"，所以分开并写明口径。
 */
export function LevelReportPage() {
  const status = useAuthStatus();

  // 恢复登录态期间不要直接渲染"未登录"：已登录用户每次刷新都会看到一次登录引导
  if (status === 'idle' || status === 'loading') {
    return (
      <section className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="text-2xl font-bold">水平报告</h1>
        <p role="status" className="mt-2 text-sm text-muted-foreground">
          正在恢复登录状态…
        </p>
      </section>
    );
  }

  if (status !== 'authenticated') {
    return (
      <AuthRequiredNotice
        feature="水平报告"
        description="水平评估需要登录：它依据你的查词记录、生词本、复习正确率与阅读速度估算 CEFR 等级与词汇量区间。未登录时这些行为没有被记录，无法估算。"
      />
    );
  }

  return <LevelReportContent />;
}

function LevelReportContent() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);

  const report = useQuery({
    queryKey: queryKeys.levelReport,
    queryFn: () => levelApi.report({ history: true, limit: 20 }),
  });

  const assess = useMutation({
    mutationFn: () => levelApi.assess(),
    onSuccess: async (data) => {
      const { cefr, vocabEstimate, cefrConfidence } = data.assessment;
      setNotice(
        `评估完成：当前 ${cefr}，词汇量约 ${vocabEstimate.toLocaleString('zh-CN')} 词，置信度 ${Math.round(cefrConfidence * 100)}%。`,
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.levelReport });
      // 个人中心与首页都显示 `currentCefr`，评估后它们必须跟着变
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
  });

  const latest = report.data?.latest ?? null;

  return (
    <section className="mx-auto max-w-3xl px-5 py-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <GraduationCap aria-hidden className="size-6 text-primary" />
            水平报告
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            结论来自你的真实学习行为（读过的文章难度、每 100 词查词次数、复习保持率、阅读速度），
            因此它可解释、可复现 —— 下面每一项都能追到依据。
          </p>
        </div>
        <Button onClick={() => assess.mutate()} loading={assess.isPending} disabled={report.isPending}>
          <RotateCw aria-hidden className="size-4" />
          重新评估
        </Button>
      </header>

      {notice && (
        <p role="status" className="mt-4 rounded-md border bg-muted/40 px-3 py-2 text-sm">
          {notice}
        </p>
      )}

      {assess.isError && (
        <p
          role="alert"
          className="mt-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          评估失败：{toDisplayMessage(assess.error)}
        </p>
      )}

      {report.isPending ? (
        <LoadingBlock className="mt-6" label="正在读取水平报告…" />
      ) : report.isError ? (
        <ErrorBlock className="mt-6" message={toDisplayMessage(report.error)} onRetry={() => void report.refetch()} />
      ) : (
        <div className="mt-5 space-y-4">
          {latest === null ? (
            <EmptyBlock
              title="还没有评估过"
              description="评估会读一遍你的阅读、查词与复习记录，算出 CEFR 等级、词汇量区间和薄弱项。数据越少结论越保守，多读几篇再评估会更准。"
              action={
                <Button size="lg" onClick={() => assess.mutate()} loading={assess.isPending}>
                  开始评估
                </Button>
              }
            />
          ) : (
            <>
              <LevelGauge assessment={latest} />

              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CalendarClock aria-hidden className="size-3.5 shrink-0" />
                {report.data?.nextAssessHint}
              </p>

              <TrendChart history={report.data?.history ?? []} />

              <WeaknessList weaknesses={latest.weaknesses} />

              <PathCard steps={latest.path} />
            </>
          )}

          {/* 特征明细：即使还没评估过也展示（features 一直是实时值），
              这样用户能先看到"平台到底在算什么" */}
          <FeaturesCard features={report.data?.features ?? latest?.features} />

          {latest?.summaryText && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info aria-hidden className="size-4 text-primary" />
                  结论说明
                </CardTitle>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-muted-foreground">{latest.summaryText}</p>
              </CardBody>
            </Card>
          )}

          <p className="text-xs text-muted-foreground">
            想看更细的逐句拆解？去
            <Link to={ROUTES.sentences} className="mx-1 text-primary underline underline-offset-4">
              长难句实验室
            </Link>
            ；想按等级选文章？去
            <Link to={ROUTES.advice} className="mx-1 text-primary underline underline-offset-4">
              学习建议
            </Link>
            。
          </p>
        </div>
      )}
    </section>
  );
}

/** 学习路径：带序号的步骤卡，并把总天数加出来（用户据此排计划）。 */
function PathCard({ steps }: { steps: StudyPathStepView[] }) {
  const totalDays = steps.reduce((sum, step) => sum + step.days, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>学习路径</CardTitle>
        <Badge tone="primary" title="各步骤建议天数的合计（步骤可并行，因此不等于必须花这么多天）">
          合计 {totalDays} 天
        </Badge>
      </CardHeader>
      <CardBody>
        {steps.length === 0 ? (
          <p className="text-xs text-muted-foreground">还没有生成路径，先做一次评估。</p>
        ) : (
          <ol className="space-y-3">
            {steps.map((step) => (
              <li key={`${step.order}-${step.title}`} className="flex gap-3">
                <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary tabular-nums">
                  {step.order}
                </span>
                <div className="min-w-0 flex-1 rounded-lg border p-3">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                    {step.title}
                    <Badge tone="muted">{step.days} 天</Badge>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * 特征明细。
 *
 * 用中文标签 + 数值的 `dl` 而不是图表：这些数字本身没有"形状"，
 * 用户要的是"平台在读哪些指标"以及"我现在是多少"。空值显示 "—" 而不是 0
 * （"阅读速度 0 词/分钟" 与 "还算不出来" 是两件事）。
 */
function FeaturesCard({ features }: { features: LevelFeatures | undefined }) {
  if (!features) return null;

  const rows: { label: string; value: string; hint?: string }[] = [
    { label: '已读文章数', value: String(features.articleCount), hint: '有阅读进度记录的' },
    { label: '读完的文章', value: String(features.finishedArticles) },
    { label: '生词本规模', value: String(features.wordRecords) },
    { label: '已掌握词数', value: String(features.masteredWords) },
    {
      label: '每 100 词查词次数',
      value: features.lookupsPer100Words.toFixed(1),
      hint: '越高说明读得越吃力',
    },
    {
      label: '复习保持率',
      value: features.retentionRate === null ? '—' : `${Math.round(features.retentionRate * 100)}%`,
      hint: '复习中「记得」及以上的比例',
    },
    {
      label: '阅读速度',
      value: features.readingWpm === null ? '—' : `${Math.round(features.readingWpm)} 词/分钟`,
    },
    {
      label: '读过文章平均难度',
      value: features.avgArticleDifficulty === null ? '—' : features.avgArticleDifficulty.toFixed(0),
      hint: '0–100',
    },
    { label: '已复习卡片数', value: String(features.reviewedCards) },
    {
      label: '标记不认识/模糊的比例',
      value: `${Math.round(features.unknownRatio * 100)}%`,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>评估依据的特征（实时值）</CardTitle>
        <span className="text-xs text-muted-foreground">上面的结论来自评估时的快照</span>
      </CardHeader>
      <CardBody>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
          {rows.map((row) => (
            <div key={row.label}>
              <dt className="text-xs text-muted-foreground">{row.label}</dt>
              <dd className="text-sm font-semibold tabular-nums">{row.value}</dd>
              {row.hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{row.hint}</p>}
            </div>
          ))}
        </dl>
      </CardBody>
    </Card>
  );
}
